import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Shared helpers for the receipt-copy editors (loyalty progress + comeback
 * coupon message). Restaurant owners shouldn't have to hand-type mustache
 * placeholders like `{{remaining}}`; instead they tap a labelled chip and we
 * insert the raw token at the caret. The receipt renderer still swaps the
 * token for the real value at print time.
 */

/** Insert `token` at the current caret position of a text field, preserving
 *  the surrounding text, then restore focus + caret just after the token. */
export function insertAtCursor(
  el: HTMLTextAreaElement | HTMLInputElement | null,
  token: string,
  current: string,
  setValue: (next: string) => void,
) {
  if (!el) {
    setValue(current + token)
    return
  }
  const start = el.selectionStart ?? current.length
  const end = el.selectionEnd ?? current.length
  const next = current.slice(0, start) + token + current.slice(end)
  setValue(next)
  // Defer caret restore until after React re-renders the controlled value.
  requestAnimationFrame(() => {
    el.focus()
    const pos = start + token.length
    try {
      el.setSelectionRange(pos, pos)
    } catch {
      /* some field types don't support selection ranges — ignore */
    }
  })
}

/** A row of "tap to insert" chips. `onMouseDown` preventDefault keeps focus
 *  (and the caret) in the text field so the token lands where the owner left
 *  off rather than at the end. */
export function TokenChips({
  tokens,
  onInsert,
  className,
}: {
  tokens: { label: string; token: string }[]
  onInsert: (token: string) => void
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Insert
      </span>
      {tokens.map((t) => (
        <button
          key={t.token}
          type="button"
          title={`Insert ${t.token}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onInsert(t.token)}
          className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1 text-sm font-semibold text-primary shadow-sm transition-colors hover:border-primary/60 hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <Plus className="h-3 w-3 shrink-0" aria-hidden />
          {t.label}
        </button>
      ))}
    </div>
  )
}

/** Receipt preview — close visual approximation of what prints on thermal
 *  paper for a single receipt coupon. Mirrors thankYouPromoSection in
 *  printCustomerReceipt.ts: stars-flanked headline (with padding around the
 *  title), two-column body (multi-line message + auto-appended arrow on the
 *  left, double-bordered code box on the right), terms below.
 *
 *  Shared by the comeback ("SEE YOU SOON") and recovery ("OUR TREAT NEXT
 *  TIME") editors — only the headline (and whether a min-subtotal term
 *  shows) differs, so it takes both as props. */
export function ReceiptCouponPreview({
  headline,
  message,
  code,
  expiry,
  minSubtotal,
}: {
  headline: string
  message: string
  code: string
  expiry: string
  minSubtotal?: number | null
}) {
  const terms = [`Valid until ${expiry}`]
  if (minSubtotal && minSubtotal > 0) {
    terms.push(`Min order Rs. ${Math.round(minSubtotal)}`)
  }
  // Split admin message on \n so blank lines render as the same blank lines
  // that print on the slip. The arrow is appended visually after the FINAL
  // non-empty line, not after every line.
  const messageLines = message.split('\n')
  return (
    <div className="rounded-md border border-dashed border-foreground/30 bg-background px-4 py-5">
      <div className="border-y border-dashed border-foreground/60 px-2 py-4">
        <div className="text-center text-sm font-extrabold uppercase tracking-wider text-foreground">
          <span>★</span>
          <span className="inline-block px-1.5">{headline}</span>
          <span>★</span>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 text-sm font-semibold leading-snug text-foreground">
            {messageLines.map((line, i) => {
              const isLast = i === messageLines.length - 1
              return (
                <div key={i} className={line === '' ? 'h-2' : ''}>
                  {line}
                  {isLast && line !== '' && (
                    <>
                      {' '}
                      <span className="font-extrabold" aria-hidden="true">
                        →
                      </span>
                    </>
                  )}
                </div>
              )
            })}
            <div className="mt-1.5 text-sm font-medium tracking-tight text-foreground/70">
              {terms.join(' · ')}
            </div>
          </div>
          <div className="shrink-0">
            <div
              className="px-3 py-2 text-center font-mono text-sm font-extrabold tracking-widest text-foreground"
              style={{ border: '3px double currentColor' }}
            >
              {code}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
